import React from "react";

import ChildrensBookManuscriptIllustrationContext from "./ChildrensBookManuscriptIllustrationContext";

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

export function manuscriptFullPageSpreadLayoutApplies(
  unit = {}
) {
  const unitType =
    clean(
      unit?.unitType
    ).toLowerCase();

  const illustrationPlan =
    clean(
      unit?.illustrationPlan
    ).toLowerCase();

  const illustrationPageSide =
    clean(
      unit?.illustrationPageSide
    ).toLowerCase();

  return (
    unitType ===
      "double_page_spread" &&
    illustrationPlan ===
      "full_page" &&
    (
      illustrationPageSide ===
        "left" ||
      illustrationPageSide ===
        "right"
    )
  );
}

export function manuscriptPageComposition(
  unit = {}
) {
  const source =
    (
      unit?.pageComposition &&
      typeof unit.pageComposition ===
        "object" &&
      !Array.isArray(
        unit.pageComposition
      )
    )
      ? unit.pageComposition
      : {};

  return {
    title:
      clean(
        source.title
      ),
    subtitle:
      clean(
        source.subtitle
      ),
    caption:
      clean(
        source.caption
      ),
    creditLine:
      clean(
        source.creditLine
      ),
  };
}


function validPageNumber(
  value
) {
  const number =
    Number(value);

  return (
    Number.isInteger(number) &&
    number > 0
  )
    ? number
    : null;
}

export default function ChildrensBookManuscriptFullPageSpreadLayout({
  lang = "en",
  unit,
  pageStart,
  pageEnd,
  childrensBookAssets = [],
  onLoadChildrensBookAssetFile,
  children,
}) {
  if (
    !manuscriptFullPageSpreadLayoutApplies(
      unit
    )
  ) {
    return children;
  }

  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const leftPage =
    validPageNumber(
      pageStart
    );

  const rightPage =
    validPageNumber(
      pageEnd
    );

  const illustrationPageSide =
    clean(
      unit?.illustrationPageSide
    ).toLowerCase();

  const pageComposition =
    manuscriptPageComposition(
      unit
    );

  const hasPageHeading =
    Boolean(
      pageComposition.title ||
      pageComposition.subtitle
    );

  const hasPageFooter =
    Boolean(
      pageComposition.caption ||
      pageComposition.creditLine
    );

  const pages = [
    {
      side: "left",
      pageNumber:
        leftPage,
    },
    {
      side: "right",
      pageNumber:
        rightPage,
    },
  ];

  return (
    <section
      data-testid="manuscript-full-page-spread-layout"
      role="group"
      aria-label={
        isFR
          ? "Double page manuscrit et illustration"
          : "Manuscript and illustration spread"
      }
      style={{
        display: "grid",
        gridTemplateColumns:
          "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 18,
        alignItems:
          "stretch",
        maxWidth: 1120,
        width: "100%",
        margin:
          "0 auto",
      }}
    >
      {pages.map(
        ({
          side,
          pageNumber,
        }) => {
          const illustrationPage =
            side ===
            illustrationPageSide;

          const pageLabel =
            pageNumber
              ? `Page ${pageNumber}`
              : (
                  isFR
                    ? "Page"
                    : "Page"
                );

          return (
            <article
              key={side}
              data-testid="manuscript-spread-page-panel"
              data-page-side={side}
              data-page-role={
                illustrationPage
                  ? "illustration"
                  : "manuscript"
              }
              aria-label={
                illustrationPage
                  ? (
                      isFR
                        ? `${pageLabel} — illustration pleine page`
                        : `${pageLabel} — Full-page illustration`
                    )
                  : (
                      isFR
                        ? `${pageLabel} — manuscrit`
                        : `${pageLabel} — Manuscript`
                    )
              }
              style={{
                display:
                  "flex",
                flexDirection:
                  "column",
                minHeight: 650,
                minWidth: 0,
                padding: 20,
                boxSizing:
                  "border-box",
                borderRadius: 14,
                border:
                  "1px solid #94a3b8",
                background:
                  "#ffffff",
                boxShadow:
                  "0 12px 28px rgba(15,23,42,0.10)",
              }}
            >
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "flex-start",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <strong
                  style={{
                    fontSize: 22,
                  }}
                >
                  {pageLabel}
                </strong>

                <span
                  className="ns-card-meta"
                  style={{
                    fontWeight:
                      800,
                  }}
                >
                  {illustrationPage
                    ? (
                        isFR
                          ? "Illustration"
                          : "Illustration"
                      )
                    : (
                        isFR
                          ? "Manuscrit"
                          : "Manuscript"
                      )}
                </span>
              </div>

              {illustrationPage ? (
                <div
                  data-testid="manuscript-composed-illustration-page"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection:
                      "column",
                    gap: 14,
                  }}
                >
                  {hasPageHeading ? (
                    <header
                      data-testid="manuscript-page-composition-heading"
                      style={{
                        textAlign:
                          "center",
                        padding:
                          "2px 10px 0",
                      }}
                    >
                      {pageComposition
                        .title ? (
                        <h3
                          data-testid="manuscript-page-composition-title"
                          style={{
                            margin: 0,
                            fontFamily:
                              "Georgia, 'Times New Roman', serif",
                            fontSize:
                              "clamp(23px, 2.5vw, 34px)",
                            lineHeight:
                              1.16,
                            color:
                              "#172033",
                          }}
                        >
                          {
                            pageComposition
                              .title
                          }
                        </h3>
                      ) : null}

                      {pageComposition
                        .subtitle ? (
                        <p
                          data-testid="manuscript-page-composition-subtitle"
                          style={{
                            margin:
                              pageComposition
                                .title
                                ? "7px 0 0"
                                : 0,
                            fontSize: 16,
                            lineHeight:
                              1.45,
                            color:
                              "#475569",
                          }}
                        >
                          {
                            pageComposition
                              .subtitle
                          }
                        </p>
                      ) : null}
                    </header>
                  ) : null}

                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <ChildrensBookManuscriptIllustrationContext
                      lang={lang}
                      unit={unit}
                      childrensBookAssets={
                        childrensBookAssets
                      }
                      onLoadChildrensBookAssetFile={
                        onLoadChildrensBookAssetFile
                      }
                      presentation="page"
                    />
                  </div>

                  {hasPageFooter ? (
                    <footer
                      data-testid="manuscript-page-composition-footer"
                      style={{
                        display: "grid",
                        gap: 5,
                        textAlign:
                          "center",
                        padding:
                          "0 12px 2px",
                      }}
                    >
                      {pageComposition
                        .caption ? (
                        <div
                          data-testid="manuscript-page-composition-caption"
                          style={{
                            fontSize: 14,
                            lineHeight:
                              1.45,
                            color:
                              "#334155",
                          }}
                        >
                          {
                            pageComposition
                              .caption
                          }
                        </div>
                      ) : null}

                      {pageComposition
                        .creditLine ? (
                        <div
                          data-testid="manuscript-page-composition-credit"
                          style={{
                            fontSize: 12,
                            lineHeight:
                              1.4,
                            color:
                              "#64748b",
                          }}
                        >
                          {
                            pageComposition
                              .creditLine
                          }
                        </div>
                      ) : null}
                    </footer>
                  ) : null}
                </div>
              ) : (
                <div
                  data-testid="manuscript-spread-writing-surface"
                  style={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {children}
                </div>
              )}
            </article>
          );
        }
      )}
    </section>
  );
}
